import { useEffect, useState } from 'react';
import type * as FavCache from '@finn-no/favorite-api-browser-client';
import { i18n } from '@lingui/core';

const promoteAdIdsInSortPure = (
    folderIds: number[],
    favs: FavCache.FavoriteListType[],
): FavCache.FavoriteListType[] => {
    const favsComparator =
        (folderIds: number[]) =>
        (a: FavCache.FavoriteListType, b: FavCache.FavoriteListType) => {
            if (a.default) {
                return -1;
            } else if (b.default) {
                return 1;
            } else if (
                folderIds.includes(a.folderid) &&
                folderIds.includes(b.folderid)
            ) {
                return 0;
            } else if (folderIds.includes(a.folderid)) {
                return -1;
            } else if (folderIds.includes(b.folderid)) {
                return 1;
            } else {
                return 0;
            }
        };
    if (folderIds) {
        const cmp = favsComparator(folderIds);
        return favs.sort(cmp);
    } else {
        return favs;
    }
};

export { promoteAdIdsInSortPure };

type useFetchFoldersProps = {
    api: FavCache.FavoriteBrowserCache;
    itemType: FavCache.ItemType;
    itemId: number;
    userId?: FavCache.UserId;
    isOpen: boolean;
};

const useFetchFolders = ({
    api,
    itemType,
    itemId,
    userId,
    isOpen,
}: useFetchFoldersProps) => {
    const [items, setItems] = useState<FavCache.FavoriteListType[]>([]);
    const [foldersWithItem, setFoldersWithItem] = useState<FavCache.ItemFolder>(
        {
            itemType: itemType,
            itemId: itemId,
            folderIds: [],
        },
    );
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        let didCancel = false;
        async function fetchFolders() {
            try {
                const [foldersWithItem, folders] = await Promise.all([
                    api.fetchFoldersForItem(itemType, itemId),
                    api.fetchFolders().then((res) => res.json()),
                ]);
                if (!didCancel) {
                    setFoldersWithItem(foldersWithItem);
                    const sortedList = promoteAdIdsInSortPure(
                        foldersWithItem.folderIds,
                        folders,
                    );
                    setItems(sortedList);
                }
            } catch (error: unknown) {
                console.error(error);
                setErrorMessage(
                    i18n.t({
                        id: 'genericListErrorMessage',
                    }),
                );
            }
        }

        if (userId) {
            fetchFolders();

            return () => {
                didCancel = true;
            };
        }
    }, [itemType, itemId, api, userId, isOpen]);

    return { items, errorMessage, foldersWithItem };
};

export default useFetchFolders;
